import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { echo } from '@laravel/echo-vue'
import { captureException } from '@sentry/vue'
import { reject as _reject, forEach } from 'lodash'
import moment from 'moment'
import { useFarmerStore } from '@/stores/admin/farmer.js'
import { useNotificationStore } from '@/stores/helpers/notification.js'
import AuthService from '@/services/admin/AuthService.js'
import UserService from '@/services/admin/UserService.js'
import { eventEmitter } from '@/event-bus.js'
import i18n from '@/i18n/i18n.js'
import { EchoManager } from '@/utilities/echo-manager.js'
import { errorMessage } from '@/utilities/helpers.js'
import { AUTH_ERROR_MESSAGES } from '@/constants/errorMessages.js'

const { t } = i18n.global

export const useAdminAuthStore = defineStore('adminAuth', () => {
  const notificationStore = useNotificationStore()

  const permissions = ref([])
  const user = ref(null)
  const regions = ref([])
  const token = ref(null)
  const isAdmin = ref(false)
  const unread_notifications = ref([])
  const unseen_notification_count = ref('0')
  const nextRoute = ref(null)

  const getToken = computed(() => {
    return token.value ? token.value : localStorage.getItem('user_access_token')
  })

  const getNextRoute = computed(() => {
    return nextRoute.value
  })

  const hasPermission = permission => {
    return isAdmin.value || permissions.value.includes(permission)
  }

  const hasAnyPermission = permissionList => {
    return (
      isAdmin.value ||
      (Array.isArray(permissionList) && permissionList.some(permission => permissions.value.includes(permission)))
    )
  }

  // A user is restricted to their assigned regions when they belong to at least one region
  // and lack the permission to view records across all regions.
  const isRegionRestricted = computed(() => {
    return regions.value.length > 0 && hasPermission('view-orders-in-all-regions') === false
  })

  const setToken = newToken => {
    token.value = newToken
    localStorage.setItem('user_access_token', newToken)
    EchoManager.updateToken(newToken)
  }

  // Sync in-memory token/user from localStorage to recover from cross-tab logins before getUser hits 401.
  const syncFromStorage = () => {
    const localToken = localStorage.getItem('user_access_token')
    if (localToken && localToken !== token.value) {
      token.value = localToken
      user.value = null
      EchoManager.updateToken(localToken)
      return true
    }
    return false
  }

  let isLoggingOut = false
  let isSessionExpired = false

  const handleSessionExpired = () => {
    if (isLoggingOut === true || isSessionExpired === true) return
    isSessionExpired = true

    notificationStore.add({
      type: 'info',
      message: t('shared.auth_errors.session_expired')
    })

    token.value = null
    localStorage.removeItem('user_access_token')
    try {
      unsubscribeFromChannel()
    } catch (e) {
      captureException(e, {
        tags: {
          auth_flow: 'session_expired',
          panel: 'admin',
          action: 'unsubscribe_channel'
        }
      })
    }
    EchoManager.clearToken()
    window.location.href = '/admin/login'
  }

  const logout = path => {
    if (isLoggingOut) return
    isLoggingOut = true

    if (path) {
      localStorage.setItem('next_route_admin', path)
    }

    AuthService.logout()
      .catch(() => {
        token.value = null
        localStorage.removeItem('user_access_token')
      })
      .finally(() => {
        token.value = null
        localStorage.removeItem('user_access_token')
        try {
          unsubscribeFromChannel()
        } catch (err) {
          /* user may not be loaded */
          const notification = {
            type: 'danger',
            message: `${AUTH_ERROR_MESSAGES.userMayNotBeLoaded}: ${errorMessage(err)}`
          }
          notificationStore.add(notification)
        }
        EchoManager.clearToken()
        location.reload()
      })
  }

  const setUser = userData => {
    user.value = userData
    isAdmin.value = userData.is_admin
    regions.value = userData.regions ?? []
    unread_notifications.value = userData.unread_notifications
    permissions.value = []
    forEach(userData.roles, role => {
      forEach(role.permissions, permission => {
        permissions.value.push(permission.name)
      })
    })
  }

  const setNextRoute = path => {
    nextRoute.value = path
  }

  const getUser = async (force = false) => {
    if (user.value && !force) {
      return user.value
    }
    return new Promise((resolve, reject) => {
      AuthService.getUser()
        .then(response => {
          setUser(response.data)
          subscribeToChannel()
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: `${AUTH_ERROR_MESSAGES.getUserError}: ${errorMessage(err)}`
          }
          notificationStore.add(notification)
          reject(err)
        })
    })
  }

  const login = userData => {
    return new Promise((resolve, reject) => {
      AuthService.login(userData)
        .then(response => {
          setToken(response.access_token)
          setUser(response.user)
          subscribeToChannel()
          resolve(response.user)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: `${AUTH_ERROR_MESSAGES.loginError}: ${errorMessage(err)}`
          }
          notificationStore.add(notification)
          localStorage.clear()
          reject(err)
        })
    })
  }

  const verify = code => {
    return new Promise((resolve, reject) => {
      AuthService.verify(code)
        .then(response => {
          setToken(response.access_token)
          setUser(response.user)
          resolve(response.user)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: `${AUTH_ERROR_MESSAGES.verifyError}: ${errorMessage(err)}`
          }
          notificationStore.add(notification)
          reject(err)
        })
    })
  }

  const getNotifications = () => {
    return new Promise((resolve, reject) => {
      UserService.getNotifications(user.value.id)
        .then(response => {
          unread_notifications.value = response.data
          unseen_notification_count.value = response.meta.unseen_notification_count
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: `${AUTH_ERROR_MESSAGES.getUserError}: ${errorMessage(err)}`
          }
          notificationStore.add(notification)
          reject(err)
        })
    })
  }

  const markAsReadNotifications = () => {
    return UserService.markAsReadNotifications(user.value.id).then(() => {
      unread_notifications.value = []
    })
  }

  const markAsReadNotification = id => {
    return UserService.markAsReadNotification(user.value.id, id).then(() => {
      unread_notifications.value = _reject(unread_notifications.value, item => {
        return item.id === id
      })
    })
  }

  const markAsSeenNotifications = () => {
    UserService.markAsSeenNotifications(user.value.id).then(() => {
      unseen_notification_count.value = 0
    })
  }

  const addNotification = notification => {
    const content = {
      data: {},
      created_at: moment()
    }
    forEach(notification, (n, k) => {
      if (k === 'id' || k === 'type') {
        content[k] = n
      } else {
        content.data[k] = n
      }
    })
    unread_notifications.value.unshift(content)
    unseen_notification_count.value++
  }

  const subscribeToChannel = () => {
    const echoInstance = echo()
    echoInstance.private(`App.Models.User.${user.value.id}`).notification(notification => {
      const notificationContent = {
        type: 'info',
        message: notification?.message
      }
      notificationStore.add(notificationContent)
      addNotification(notification)

      if (notification.type === 'App\\Models\\FarmerGtsRequestCompleted') {
        const farmerStore = useFarmerStore()
        farmerStore.getGtsData(notification.farmer_id)
        eventEmitter.emit('gtsRequestCompleted')
      }
    })
  }

  const unsubscribeFromChannel = () => {
    const echoInstance = echo()
    echoInstance.leave(`App.Models.User.${user.value.id}`)
  }

  return {
    permissions,
    user,
    regions,
    token,
    isAdmin,
    unread_notifications,
    unseen_notification_count,
    nextRoute,
    getToken,
    getNextRoute,
    hasPermission,
    hasAnyPermission,
    isRegionRestricted,
    setToken,
    syncFromStorage,
    logout,
    handleSessionExpired,
    setUser,
    setNextRoute,
    getUser,
    login,
    verify,
    getNotifications,
    markAsReadNotifications,
    markAsReadNotification,
    markAsSeenNotifications,
    addNotification,
    subscribeToChannel,
    unsubscribeFromChannel
  }
})
